import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatErr, previewUrl, rupiah } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";

export default function CartPage() {
  const { items, remove, clear, total } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [quote, setQuote] = useState(null);
  const ids = items.map((i) => i.id).join(",");
  const isMember = user && user.role === "member";

  useEffect(() => {
    setPromo(null);
    setQuote(null);
    if (!isMember || !ids) return;
    api.post("/cart/quote", { photo_ids: ids.split(",") }).then((r) => setQuote(r.data)).catch(() => {});
  }, [ids, isMember]);

  const applyPromo = async () => {
    if (!user) return nav("/login");
    try {
      const { data } = await api.post("/cart/quote", { photo_ids: items.map((i) => i.id), promo_code: code });
      setQuote(data);
      setPromo({ code: data.promo_code, discount: data.discount });
      toast.success(`Kode ${data.promo_code} dipakai: hemat ${rupiah(data.discount)}`);
    } catch (e) {
      setPromo(null);
      toast.error(formatErr(e));
    }
  };

  const checkout = async () => {
    if (!user) return nav("/login");
    setLoading(true);
    try {
      const { data } = await api.post("/orders", { photo_ids: items.map((i) => i.id), promo_code: promo?.code || null });
      clear();
      toast.success(`${data.length} pesanan dibuat. Silakan transfer & unggah bukti.`);
      nav("/pesanan");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label="Checkout" title="Keranjang" />
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white py-20 text-center" data-testid="cart-empty">
          <p className="text-zinc-500 mb-4">Keranjang masih kosong.</p>
          <Button asChild variant="outline" className="rounded-xl"><Link to="/cari">Cari foto wajahmu</Link></Button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-3">
            {items.map((i) => (
              <div key={i.id} className="flex items-center gap-4 rounded-2xl bg-white border border-zinc-200 p-3" data-testid={`cart-item-${i.id}`}>
                <img src={previewUrl(i.id)} alt="" className="h-20 w-28 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{i.event_title}</p>
                  <p className="text-sm text-zinc-500">{i.photographer_name}</p>
                </div>
                <span className="font-mono-x text-sm">{rupiah(i.price)}</span>
                <Button variant="ghost" size="icon" onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`}><Trash2 className="h-4 w-4 text-zinc-500" /></Button>
              </div>
            ))}
          </div>
          <aside className="rounded-2xl bg-white border border-zinc-200 p-6 h-fit sticky top-24">
            <p className="text-sm text-zinc-500">{items.length} foto</p>
            {quote && quote.total < quote.regular_total && <p className="text-sm text-zinc-400 line-through mt-1" data-testid="cart-regular-total">{rupiah(quote.regular_total)}</p>}
            <p className="font-display text-3xl font-extrabold mt-1" data-testid="cart-total">{rupiah(quote ? quote.total : total)}</p>
            {quote?.package_savings > 0 && (
              <div className="mt-3 rounded-xl bg-blue-50 border border-blue-200 px-3 py-2 text-sm space-y-1" data-testid="cart-package-savings">
                {quote.lines.filter((l) => l.applied.length).map((l) => (
                  <p key={l.event_id} className="text-blue-800">{l.applied.map((a) => `Paket ${a.count} foto`).join(" + ")} · {l.event_title}</p>
                ))}
                <p className="font-semibold text-blue-900">Hemat paket {rupiah(quote.package_savings)}</p>
              </div>
            )}
            <div className="mt-5 flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Kode promo" className="h-10 rounded-xl font-mono-x" data-testid="cart-promo-input" />
              <Button variant="outline" onClick={applyPromo} disabled={!code} className="rounded-xl h-10" data-testid="cart-promo-apply-button">Pakai</Button>
            </div>
            {promo && (
              <div className="mt-3 flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm" data-testid="cart-promo-applied">
                <span className="text-emerald-800"><span className="font-mono-x font-bold">{promo.code}</span> · -{rupiah(promo.discount)}</span>
                <button onClick={() => { setPromo(null); setCode(""); setQuote((q) => q && { ...q, discount: 0, total: q.subtotal, promo_code: null }); }} className="text-emerald-700" data-testid="cart-promo-remove"><X className="h-4 w-4" /></button>
              </div>
            )}
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">Pesanan dipisah per fotografer. Pembayaran via transfer bank manual ke rekening fotografer, lalu unggah bukti transfer.</p>
            <Button onClick={checkout} disabled={loading} className="w-full mt-6 h-11 rounded-xl bg-blue-600 hover:bg-blue-700" data-testid="cart-checkout-button">
              {loading ? "Memproses..." : user ? "Buat Pesanan" : "Masuk untuk checkout"}
            </Button>
          </aside>
        </div>
      )}
    </main>
  );
}
